import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, MapPin, ExternalLink, Mail, Search, ArrowRight, Quote, Tag,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  hospital: 'Hospitals',
  snf: 'Skilled Nursing',
  alf: 'Assisted Living',
  home_health_agency: 'Home Health',
  hospice_agency: 'Hospice',
  physician_group: 'Physician Groups',
  payer: 'Payers',
  aco: 'ACOs',
  ipa: 'IPAs',
  fqhc: 'FQHCs',
  imaging_center: 'Imaging',
  lab: 'Labs',
  dme_supplier: 'DME',
  pharmacy: 'Pharmacy',
  other: 'Other',
};

const TIER_PILL = {
  preferred: 'bg-cn-primary text-white',
  standard: 'bg-cn-bone-100 text-cn-text border border-cn-border',
  referral_only: 'bg-amber-50 text-amber-800 border border-amber-200',
  pilot: 'bg-blue-50 text-blue-700 border border-blue-200',
};

/* ════════════════════════════════════════════════════════════════════
   PUBLIC DIRECTORY (HUB)
   ═════════════════════════════════════════════════════════════════ */
export default function PartnerDirectoryPage() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (filterType) params.partner_type = filterType;
        if (search.trim()) params.q = search.trim();
        const { data } = await axios.get(`${API}/network/directory`, { params });
        setData(data);
      } catch (e) {
        setData({ count: 0, by_type: {}, partners: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [filterType, search]);

  const partners = data?.partners || [];

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="partner-directory-page">
      <Seo
        title="Partner Directory · Hospitals, agencies and clinics on CaraLoom"
        description="The post-acute care ecosystem on one operating system. Every hospital, home-health agency, hospice team, physician group, and payer that runs on CaraLoom — discoverable, searchable, partnership-ready."
        path="/partners"
        keywords={['CaraLoom partner directory', 'post-acute partner network', 'hospital partners', 'home-health agency partners']}
        schema={[
          organizationSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Partners', path: '/partners' },
          ]),
        ]}
      />
      <PublicNavbar />

      {/* HERO */}
      <section className="cn-container pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="cn-eyebrow mb-3">Partner Directory</div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-5">
            The post-acute network, in one place.
          </h1>
          <p className="text-lg text-cn-text-muted leading-relaxed">
            Every hospital, agency, hospice team, physician group, and payer that runs on
            CaraLoom — discoverable and searchable so case managers can verify partnerships,
            BD teams can find peers, and procurement can validate the network.
          </p>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="cn-container pb-6">
        <div className="cn-card p-4 flex flex-wrap items-center gap-3" data-testid="partner-directory-controls">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cn-text-muted" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, specialty, or description"
              className="pl-9"
              data-testid="partner-directory-search"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-cn-border rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="partner-directory-type-filter"
          >
            <option value="">All partner types</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}{data?.by_type?.[k] ? ` (${data.by_type[k]})` : ''}</option>
            ))}
          </select>
          <span className="text-xs text-cn-text-muted ml-auto">{partners.length} partner{partners.length === 1 ? '' : 's'}</span>
        </div>
      </section>

      {/* GRID */}
      <section className="cn-container pb-20">
        {loading ? (
          <div className="cn-card p-12 text-center text-cn-text-muted text-sm" data-testid="partner-directory-loading">Loading partners…</div>
        ) : partners.length === 0 ? (
          <div className="cn-card p-12 text-center" data-testid="partner-directory-empty">
            <Building2 className="w-8 h-8 text-cn-text-muted mx-auto mb-2" />
            <p className="text-sm text-cn-text-muted">No partners match this filter. Try a broader search.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partner-directory-grid">
            {partners.map((p) => (
              <Link
                key={p.id}
                to={`/partners/${p.slug}`}
                className="cn-card p-5 rounded-2xl hover:shadow-cn-floating transition-shadow"
                data-testid={`partner-card-${p.slug}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {p.logo_url ? (
                      <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl object-contain bg-cn-bone-50" />
                    ) : (
                      <span className="w-12 h-12 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                        <Building2 className="w-5 h-5" />
                      </span>
                    )}
                    <div>
                      <p className="font-display font-bold text-base text-cn-text line-clamp-2">{p.name}</p>
                      <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{TYPE_LABEL[p.partner_type] || p.partner_type}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${TIER_PILL[p.tier] || ''}`}>
                    {p.tier}
                  </span>
                </div>
                {(p.city || p.state) && (
                  <p className="text-xs text-cn-text-muted inline-flex items-center gap-1 mb-2">
                    <MapPin className="w-3 h-3" /> {[p.city, p.state].filter(Boolean).join(', ')}
                  </p>
                )}
                {p.description && (
                  <p className="text-sm text-cn-text-muted line-clamp-3 mb-3">{p.description}</p>
                )}
                {(p.specialties || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {(p.specialties || []).slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 bg-cn-bone-100 border border-cn-border rounded text-cn-text-muted">{s}</span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   PUBLIC DETAIL (PER-PARTNER)
   ═════════════════════════════════════════════════════════════════ */
export function PartnerDetailPage() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await axios.get(`${API}/network/directory/${slug}`);
        setPartner(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const seoTitle = useMemo(() => (
    partner ? `${partner.name} · CaraLoom Partner Directory` : 'Partner · CaraLoom'
  ), [partner]);

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="partner-detail-page">
      <Seo
        title={seoTitle}
        description={partner?.description || 'CaraLoom partner profile.'}
        path={`/partners/${slug}`}
        schema={[
          organizationSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Partners', path: '/partners' },
            { name: partner?.name || slug, path: `/partners/${slug}` },
          ]),
        ]}
      />
      <PublicNavbar />

      {loading ? (
        <div className="cn-container py-16 text-cn-text-muted">Loading…</div>
      ) : error || !partner ? (
        <div className="cn-container py-16" data-testid="partner-detail-not-found">
          <h1 className="cn-display text-3xl text-cn-text mb-3">Partner not found</h1>
          <p className="text-cn-text-muted mb-6">This profile may have moved or been archived.</p>
          <Link to="/partners" className="cn-btn cn-btn-primary">
            Back to directory <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <>
          {/* HERO */}
          <section className="cn-container pt-14 pb-10">
            <Link to="/partners" className="text-xs font-semibold text-cn-primary inline-flex items-center gap-1 mb-4" data-testid="partner-back-link">
              ← Back to directory
            </Link>
            <div className="flex items-start gap-6 flex-wrap">
              {partner.logo_url ? (
                <img src={partner.logo_url} alt={partner.name} className="w-24 h-24 rounded-2xl object-contain bg-cn-bone-50 border border-cn-border" />
              ) : (
                <span className="w-24 h-24 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
                  <Building2 className="w-10 h-10" />
                </span>
              )}
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="cn-eyebrow">{TYPE_LABEL[partner.partner_type] || partner.partner_type}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${TIER_PILL[partner.tier] || ''}`}>
                    {partner.tier}
                  </span>
                </div>
                <h1 className="cn-display text-4xl sm:text-5xl text-cn-text mb-3" data-testid="partner-detail-name">{partner.name}</h1>
                {(partner.city || partner.state) && (
                  <p className="text-cn-text-muted inline-flex items-center gap-1 mb-2">
                    <MapPin className="w-4 h-4" /> {[partner.city, partner.state].filter(Boolean).join(', ')}
                  </p>
                )}
                {partner.description && (
                  <p className="text-base text-cn-text-muted leading-relaxed max-w-2xl mt-2">{partner.description}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {partner.intake_url && (
                  <a href={partner.intake_url} target="_blank" rel="noreferrer" className="cn-btn cn-btn-primary" data-testid="partner-intake-cta">
                    Send a referral <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                {partner.website_url && (
                  <a href={partner.website_url} target="_blank" rel="noreferrer" className="cn-btn cn-btn-ghost" data-testid="partner-website-link">
                    Visit website <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {partner.contact_email && (
                  <a href={`mailto:${partner.contact_email}`} className="cn-btn cn-btn-ghost" data-testid="partner-contact-link">
                    <Mail className="w-4 h-4" /> Contact
                  </a>
                )}
              </div>
            </div>
          </section>

          {/* SPECIALTIES + QUOTE */}
          {(partner.specialties?.length > 0 || partner.quote) && (
            <section className="cn-container pb-10">
              <div className="grid lg:grid-cols-2 gap-6">
                {(partner.specialties || []).length > 0 && (
                  <div className="cn-card p-6 rounded-2xl">
                    <h3 className="font-display font-semibold text-base text-cn-text mb-3 inline-flex items-center gap-2">
                      <Tag className="w-4 h-4 text-cn-primary" /> Service lines
                    </h3>
                    <div className="flex flex-wrap gap-2" data-testid="partner-specialties">
                      {partner.specialties.map((s) => (
                        <span key={s} className="text-xs px-2.5 py-1 bg-cn-bone-100 border border-cn-border rounded-full text-cn-text">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {partner.quote && (
                  <div className="cn-card p-6 rounded-2xl">
                    <Quote className="w-5 h-5 text-cn-primary mb-3" />
                    <p className="text-cn-text italic leading-relaxed" data-testid="partner-quote">"{partner.quote}"</p>
                    {partner.quote_attribution && (
                      <p className="text-xs text-cn-text-muted mt-2 font-semibold">— {partner.quote_attribution}</p>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* CTA STRIP */}
          <section className="cn-container pb-20">
            <div className="rounded-3xl bg-cn-primary text-white p-10 text-center" data-testid="partner-detail-cta">
              <h2 className="cn-display text-3xl text-white mb-2">Get on the CaraLoom network</h2>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">Hospitals, agencies, and clinicians use CaraLoom to orchestrate every post-acute episode end-to-end. Talk to BD about joining the directory.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/pricing" className="cn-btn bg-white text-cn-primary">
                  See operator pricing <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/partners" className="cn-btn border border-white/40 text-white hover:bg-white/10">
                  Browse the directory
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
